import { useEffect, useMemo, useState } from 'react';
import { Building2, CheckCircle2, Clipboard, Mail, MapPin, MessageCircle, Phone, Send, Sparkles, X } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { supabase } from '../../lib/supabaseClient';
import { supabaseAdmin } from '../../../lib/supabaseAdmin';
import { buildWhatsAppLink, normalizeWhatsAppPhone } from '../../utils/demo/demoWhatsAppMessage';

type UpgradeContactModalProps = {
  isOpen: boolean;
  onClose: () => void;
  companyName?: string | null;
  userName?: string | null;
  userEmail?: string | null;
  userPhone?: string | null;
};

const WHATSAPP_NUMBER = '905528525205';
const PHONE_NUMBER = '0552 852 52 05';
const EMAIL = 'iletisim@ekostrateji.com';
const FULL_ADDRESS = [
  'Yesilbaglar Mahallesi, Kaptan Sokak,',
  'Methrowin Tower 17/1 D.82',
  'Pendik / ISTANBUL 34893',
].join(' ');

const adminDb = supabaseAdmin as any;

const buildContactWhatsAppMessage = (
  companyName?: string | null,
  userName?: string | null,
  userEmail?: string | null,
  userPhone?: string | null,
) => {
  const phoneLine = userPhone?.trim() ? userPhone.trim() : 'Profilde tanimli degil';
  const lines = [
    'Merhaba, demo hesabimizi tam surume gecirmek istiyoruz.',
    companyName ? `Firma: ${companyName}` : null,
    userName ? `Yetkili: ${userName}` : null,
    userEmail ? `E-posta: ${userEmail}` : null,
    `Telefon: ${phoneLine}`,
  ].filter(Boolean);

  return lines.join('\n');
};

export default function UpgradeContactModal({
  isOpen,
  onClose,
  companyName,
  userName,
  userEmail,
  userPhone,
}: UpgradeContactModalProps) {
  const [isComposeOpen, setIsComposeOpen] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [subject, setSubject] = useState('Ekostrateji Tam Surume Gecis Talebi');
  const [message, setMessage] = useState('');
  const [resolvedUserPhone, setResolvedUserPhone] = useState(userPhone?.trim() || '');

  const phoneDigits = useMemo(() => normalizeWhatsAppPhone(PHONE_NUMBER) || PHONE_NUMBER.replace(/\D+/g, ''), []);
  const supportPhoneNumber = useMemo(() => `+90${PHONE_NUMBER.replace(/\D+/g, '').replace(/^0/, '')}`, []);

  useEffect(() => {
    setResolvedUserPhone(userPhone?.trim() || '');
  }, [userPhone]);

  useEffect(() => {
    if (!isOpen || resolvedUserPhone || !userEmail) {
      return;
    }

    let active = true;

    const loadPhoneFromDemoRequest = async () => {
      try {
        const { data, error } = await adminDb
          .from('demo_requests')
          .select('phone')
          .ilike('email', userEmail)
          .order('submitted_at', { ascending: false })
          .limit(1)
          .maybeSingle();

        if (error) {
          console.warn('[UpgradeContactModal] Demo request phone lookup failed', error.message);
          return;
        }

        const phone = (data as { phone?: string } | null)?.phone;
        if (active && phone?.trim()) {
          setResolvedUserPhone(phone.trim());
        }
      } catch (error) {
        console.warn('[UpgradeContactModal] Demo request phone lookup exception', error);
      }
    };

    loadPhoneFromDemoRequest();

    return () => {
      active = false;
    };
  }, [isOpen, resolvedUserPhone, userEmail]);

  const defaultMessage = useMemo(
    () =>
      [
        'Merhaba,',
        '',
        'Demo hesabimizi tam surume gecirmek istiyoruz.',
        companyName ? `Firma: ${companyName}` : null,
        userName ? `Yetkili: ${userName}` : null,
        userEmail ? `E-posta: ${userEmail}` : null,
        `Telefon: ${resolvedUserPhone || 'Profilde tanimli degil'}`,
        '',
        'Bize donus saglarsaniz memnun oluruz.',
      ]
        .filter(Boolean)
        .join('\n'),
    [companyName, resolvedUserPhone, userEmail, userName],
  );

  if (!isOpen) {
    return null;
  }

  const whatsappLink =
    buildWhatsAppLink(WHATSAPP_NUMBER, buildContactWhatsAppMessage(companyName, userName, userEmail, resolvedUserPhone)) ||
    'https://wa.me/';
  const whatsappSupportLink = buildWhatsAppLink(phoneDigits) || `https://wa.me/${phoneDigits}`;
  const isMobileDevice =
    typeof navigator !== 'undefined' && /Android|iPhone|iPad|iPod|Mobile|Windows Phone/i.test(navigator.userAgent);

  const handleCallClick = async () => {
    try {
      const contactLink = isMobileDevice ? `tel:${supportPhoneNumber}` : whatsappSupportLink;
      window.open(contactLink, '_blank', 'noopener,noreferrer');
      toast.success(
        isMobileDevice
          ? 'Telefon arama ekrani aciliyor. Isterseniz dogrudan aramayi baslatabilirsiniz.'
          : 'WhatsApp destek hatti aciliyor. Uygulama icinden arama veya mesajlasma baslatabilirsiniz.',
      );
    } catch {
      toast('WhatsApp destek numarasi: 0552 852 52 05', { icon: '📞' });
    }
  };

  const handleOpenCompose = () => {
    setSubject('Ekostrateji Tam Surume Gecis Talebi');
    setMessage(defaultMessage);
    setIsComposeOpen(true);
  };

  const handleSendEmail = async () => {
    if (!subject.trim()) {
      toast.error('E-posta konusu zorunludur.');
      return;
    }

    if (!message.trim()) {
      toast.error('Mesaj alani bos birakilamaz.');
      return;
    }

    setIsSending(true);
    try {
      const { data, error } = await supabase.functions.invoke('send-internal-message', {
        body: {
          to: EMAIL,
          subject: subject.trim(),
          content: message.trim(),
          messageType: 'info',
          fromUser: userName ? `${userName}${companyName ? ` - ${companyName}` : ''}` : companyName || 'Demo kullanicisi',
        },
      });

      if (error) {
        throw error;
      }

      if (!data?.success) {
        throw new Error(data?.error || 'E-posta gonderilemedi.');
      }

      toast.success('Mesajiniz Ekostrateji ekibine gonderildi.');
      setIsComposeOpen(false);
    } catch (error) {
      const messageText = error instanceof Error ? error.message : 'E-posta gonderilemedi.';
      toast.error(messageText);
    } finally {
      setIsSending(false);
    }
  };

  const handleCopyEmail = async () => {
    try {
      await navigator.clipboard.writeText(EMAIL);
      toast.success('E-posta adresi kopyalandi.');
    } catch {
      toast.error('E-posta adresi kopyalanamadi.');
    }
  };

  return (
    <div className="fixed inset-0 z-[1200] flex items-center justify-center bg-slate-950/55 p-4 backdrop-blur-sm">
      <div className="absolute inset-0" onClick={onClose} aria-hidden="true" />

      <div className="relative z-[1201] flex max-h-[85vh] w-full max-w-3xl flex-col overflow-hidden rounded-[28px] border border-emerald-100 bg-white shadow-[0_30px_80px_rgba(15,23,42,0.28)]">
        <div className="shrink-0 bg-[linear-gradient(135deg,#ecfdf5_0%,#f0fdf4_48%,#ecfeff_100%)] px-6 py-6 sm:px-8">
          <div className="flex items-start justify-between gap-4">
            <div className="max-w-2xl">
              <div className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-white/90 px-3 py-1 text-xs font-bold uppercase tracking-[0.18em] text-emerald-700">
                <Sparkles className="h-3.5 w-3.5" />
                Tam Surume Gecis
              </div>
              <h2 className="mt-4 text-2xl font-black tracking-tight text-slate-900 sm:text-3xl">
                Demo hesabinizi kalici kullanima gecirelim
              </h2>
              <p className="mt-3 max-w-2xl text-sm leading-6 text-slate-700 sm:text-base">
                Demo suresi bitmeden hesabinizi tam kullanima acalim. Paket, aktivasyon ve gecis surecini size
                en hizli kanaldan paylasalim.
              </p>
            </div>

            <button
              type="button"
              onClick={onClose}
              className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 transition hover:border-slate-300 hover:text-slate-900"
              aria-label="Pencereyi kapat"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="grid gap-6 overflow-y-auto px-6 py-6 sm:px-8 lg:grid-cols-[1.15fr,0.85fr]">
          <div className="space-y-5">
            <div className="grid gap-3 sm:grid-cols-3">
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <div className="text-xs font-bold uppercase tracking-[0.14em] text-slate-500">Kullanim</div>
                <div className="mt-2 text-sm font-semibold text-slate-900">Kalici erisim</div>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <div className="text-xs font-bold uppercase tracking-[0.14em] text-slate-500">Moduller</div>
                <div className="mt-2 text-sm font-semibold text-slate-900">Tum moduller acik</div>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <div className="text-xs font-bold uppercase tracking-[0.14em] text-slate-500">Destek</div>
                <div className="mt-2 text-sm font-semibold text-slate-900">Uzman ekip destegi</div>
              </div>
            </div>

            <div className="rounded-3xl border border-emerald-100 bg-emerald-50/70 p-5">
              <h3 className="text-sm font-black uppercase tracking-[0.16em] text-emerald-800">Hazir bilgi</h3>
              <div className="mt-4 grid gap-3 sm:grid-cols-2">
                <div className="rounded-2xl border border-white bg-white px-4 py-3">
                  <div className="text-xs font-semibold text-slate-500">Firma</div>
                  <div className="mt-1 text-sm font-bold text-slate-900">{companyName || 'Secili firma bilgisi yok'}</div>
                </div>
                <div className="rounded-2xl border border-white bg-white px-4 py-3">
                  <div className="text-xs font-semibold text-slate-500">Yetkili</div>
                  <div className="mt-1 text-sm font-bold text-slate-900">{userName || 'Kullanici'}</div>
                </div>
                <div className="rounded-2xl border border-white bg-white px-4 py-3 sm:col-span-2">
                  <div className="text-xs font-semibold text-slate-500">E-posta</div>
                  <div className="mt-1 break-all text-sm font-bold text-slate-900">{userEmail || 'E-posta bilgisi yok'}</div>
                </div>
                <div className="rounded-2xl border border-white bg-white px-4 py-3 sm:col-span-2">
                  <div className="text-xs font-semibold text-slate-500">Telefon</div>
                  <div className="mt-1 break-all text-sm font-bold text-slate-900">{resolvedUserPhone || 'Telefon bilgisi yok'}</div>
                </div>
              </div>
            </div>

            <div className="flex flex-col gap-3 sm:flex-row">
              <a
                href={whatsappLink}
                target="_blank"
                rel="noreferrer"
                className="inline-flex flex-1 items-center justify-center gap-2 rounded-2xl bg-emerald-600 px-5 py-3.5 text-sm font-bold text-white transition hover:bg-emerald-700"
              >
                <MessageCircle className="h-4 w-4" />
                WhatsApp ile Iletisime Gec
              </a>
              <button
                type="button"
                onClick={handleCallClick}
                className="inline-flex flex-1 items-center justify-center gap-2 rounded-2xl border border-slate-300 bg-white px-5 py-3.5 text-sm font-bold text-slate-800 transition hover:border-slate-400 hover:bg-slate-50"
              >
                <Phone className="h-4 w-4" />
                Hemen Ara
              </button>
              <button
                type="button"
                onClick={handleOpenCompose}
                className="inline-flex flex-1 items-center justify-center gap-2 rounded-2xl border border-cyan-300 bg-cyan-50 px-5 py-3.5 text-sm font-bold text-cyan-900 transition hover:border-cyan-400 hover:bg-cyan-100"
              >
                <Mail className="h-4 w-4" />
                E-posta Gonder
              </button>
            </div>

            {isComposeOpen && (
              <div className="rounded-[28px] border border-cyan-200 bg-[linear-gradient(180deg,#f8fdff_0%,#ffffff_100%)] p-5 shadow-[0_18px_40px_rgba(14,165,233,0.08)]">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div>
                    <div className="inline-flex items-center gap-2 rounded-full border border-cyan-200 bg-white px-3 py-1 text-[11px] font-black uppercase tracking-[0.18em] text-cyan-700">
                      <Send className="h-3.5 w-3.5" />
                      Mesaj Gonder
                    </div>
                    <h3 className="mt-3 text-lg font-black text-slate-900">Ekostrateji ekibine hizli e-posta</h3>
                    <p className="mt-1 text-sm leading-6 text-slate-600">
                      Konu ve mesaj hazir. Isterseniz duzenleyip dogrudan bu pencereden gonderebilirsiniz.
                    </p>
                  </div>
                  <div className="flex flex-col gap-3 sm:items-end">
                    <div className="inline-flex items-center gap-2 rounded-2xl border border-emerald-100 bg-emerald-50 px-3 py-2 text-xs font-semibold text-emerald-900">
                      <CheckCircle2 className="h-4 w-4" />
                      Alici: {EMAIL}
                    </div>
                    <button
                      type="button"
                      disabled={isSending}
                      onClick={handleSendEmail}
                      className="inline-flex items-center justify-center gap-2 rounded-2xl bg-cyan-600 px-5 py-3 text-sm font-bold text-white transition hover:bg-cyan-700 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      <Send className="h-4 w-4" />
                      {isSending ? 'Gonderiliyor...' : 'Mesaji Gonder'}
                    </button>
                  </div>
                </div>

                <div className="mt-5 grid gap-4">
                  <label className="block">
                    <span className="mb-2 block text-xs font-bold uppercase tracking-[0.14em] text-slate-500">Konu</span>
                    <input
                      type="text"
                      value={subject}
                      onChange={(event) => setSubject(event.target.value)}
                      className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-900 outline-none transition focus:border-cyan-400 focus:ring-4 focus:ring-cyan-100"
                    />
                  </label>

                  <label className="block">
                    <span className="mb-2 block text-xs font-bold uppercase tracking-[0.14em] text-slate-500">Mesaj</span>
                    <textarea
                      value={message}
                      onChange={(event) => setMessage(event.target.value)}
                      rows={9}
                      className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm leading-6 text-slate-800 outline-none transition focus:border-cyan-400 focus:ring-4 focus:ring-cyan-100"
                    />
                  </label>

                  <div className="flex flex-col gap-3 sm:flex-row sm:justify-between">
                    <button
                      type="button"
                      onClick={handleCopyEmail}
                      className="inline-flex items-center justify-center gap-2 rounded-2xl border border-cyan-300 bg-cyan-50 px-4 py-3 text-sm font-bold text-cyan-900 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:border-cyan-400 hover:bg-cyan-100 hover:text-cyan-950 hover:shadow-[0_12px_24px_rgba(14,165,233,0.16)]"
                    >
                      <Clipboard className="h-4 w-4" />
                      Alici E-postasini Kopyala
                    </button>

                    <div className="flex flex-col gap-3 sm:flex-row">
                      <button
                        type="button"
                        onClick={() => setIsComposeOpen(false)}
                        className="inline-flex items-center justify-center rounded-2xl border border-slate-300 bg-slate-100 px-4 py-3 text-sm font-bold text-slate-800 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:border-slate-400 hover:bg-slate-200 hover:text-slate-950 hover:shadow-[0_12px_24px_rgba(15,23,42,0.12)]"
                      >
                        Vazgec
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="space-y-4">
            <div className="rounded-3xl border border-slate-200 bg-slate-50 p-5">
              <div className="flex items-center gap-2 text-sm font-black uppercase tracking-[0.16em] text-slate-700">
                <Building2 className="h-4 w-4" />
                Ekostrateji Iletisim
              </div>

              <div className="mt-4 space-y-4 text-sm text-slate-700">
                <div className="flex items-start gap-3">
                  <Phone className="mt-0.5 h-4 w-4 text-emerald-700" />
                  <div>
                    <div className="font-semibold text-slate-900">GSM</div>
                    <div>{PHONE_NUMBER}</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <MessageCircle className="mt-0.5 h-4 w-4 text-emerald-700" />
                  <div>
                    <div className="font-semibold text-slate-900">WhatsApp Destek</div>
                    <div>{PHONE_NUMBER}</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Mail className="mt-0.5 h-4 w-4 text-emerald-700" />
                  <div>
                    <div className="font-semibold text-slate-900">E-posta</div>
                    <div>{EMAIL}</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <MapPin className="mt-0.5 h-4 w-4 text-emerald-700" />
                  <div>
                    <div className="font-semibold text-slate-900">Adres</div>
                    <div className="leading-6">{FULL_ADDRESS}</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="rounded-3xl border border-amber-200 bg-amber-50 p-5">
              <div className="text-sm font-black uppercase tracking-[0.16em] text-amber-900">Onerilen yol</div>
              <p className="mt-3 text-sm leading-6 text-amber-950/90">
                En hizli donus icin WhatsApp butonunu kullanin. Hemen Ara butonu mobil cihazlarda telefon arama
                ekranini, masaustunde ise WhatsApp destek hattini acar.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
